import { useEffect, useState } from "react";
import { getInlineLoader } from "../Helpers";
import PageHero from "./PageHero";

// Image Assets
import contactHeroBg from "../assets/orphans.jpg";
import officeBg from "../assets/youth.jpg";
import communicationBg from "../assets/prisoners.jpg";

// React Icons
import { 
    FaMapMarkerAlt, 
    FaEnvelopeOpen, 
    FaGlobeAfrica, 
    FaPhoneAlt, 
    FaRegClock, 
    FaPaperPlane, 
    FaBuilding 
} from "react-icons/fa";

export default function ContactContent(props) {
    const [ready, setReady] = useState(false);
    const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });

    async function init() {
        setReady(false);
        // Initialization space if needed
        setReady(true);
    }

    useEffect(() => {
        init();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        // Form submission logic placeholder
        alert("Thank you for contacting MWADEO. We will get back to you shortly.");
        setFormData({ name: "", email: "", subject: "", message: "" });
    };

    if (ready) {
        return (
            <div className="contact-page-wrapper">
                {/* 1. Page Hero */}
                <PageHero 
                    kicker="REACH OUT TO US" 
                    title="Get in Touch with MWADEO" 
                    bgImage={contactHeroBg} 
                />

                {/* 2. Communication Channels Quick Grid */}
                <section className="contact-surface-section py-5">
                    <div className="container py-3">
                        <div className="text-center max-w-700 mx-auto mb-5">
                            <span className="badge bg-primary text-white text-uppercase letter-spacing-1 fw-bold px-3 py-2 mb-2">
                                DIRECT COMMUNICATION
                            </span>
                            <h2 className="title-font-bold text-dark display-6 m-0">
                                Contact Channels
                            </h2>
                            <div className="contact-primary-bar mx-auto mt-3"></div>
                        </div>

                        <div className="row g-4 justify-content-center">
                            {/* Phone Numbers Column */}
                            <div className="col-12 col-md-4 text-center">
                                <div className="contact-channel-card p-4 rounded-4 bg-white border shadow-sm h-100">
                                    <div className="contact-icon-bubble bg-primary-subtle text-primary mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle">
                                        <FaPhoneAlt size={22} />
                                    </div>
                                    <h5 className="title-font-semi-bold text-dark mb-2">Phone Lines</h5>
                                    <p className="font-regular text-muted small mb-0 lh-base">
                                        +255 (0) 123 456 789<br />
                                        +255 (0) 987 654 321
                                    </p>
                                </div>
                            </div>

                            {/* Email Address Column */}
                            <div className="col-12 col-md-4 text-center">
                                <div className="contact-channel-card p-4 rounded-4 bg-white border shadow-sm h-100">
                                    <div className="contact-icon-bubble bg-primary-subtle text-primary mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle">
                                        <FaEnvelopeOpen size={22} />
                                    </div>
                                    <h5 className="title-font-semi-bold text-dark mb-2">Official Email</h5>
                                    <p className="font-regular text-muted small mb-0 lh-base">
                                        info@mwadeo.or.tz<br />
                                        support@mwadeo.or.tz
                                    </p>
                                </div>
                            </div>

                            {/* Office Hours Column */}
                            <div className="col-12 col-md-4 text-center">
                                <div className="contact-channel-card p-4 rounded-4 bg-white border shadow-sm h-100">
                                    <div className="contact-icon-bubble bg-primary-subtle text-primary mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle">
                                        <FaRegClock size={22} />
                                    </div>
                                    <h5 className="title-font-semi-bold text-dark mb-2">Working Hours</h5>
                                    <p className="font-regular text-muted small mb-0 lh-base">
                                        Monday – Friday: 8:00 AM – 5:00 PM<br />
                                        Saturday & Sunday: Closed
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 3. Head Office & Information Section with Dark/Primary Overlay */}
                <section className="contact-bg-section position-relative py-5" style={{ backgroundImage: `url(${officeBg})` }}>
                    <div className="contact-primary-overlay"></div>
                    <div className="container position-relative z-index-3 py-4">
                        <div className="text-center max-w-700 mx-auto mb-5">
                            <span className="text-warning text-uppercase fw-bold letter-spacing-2 small">
                                HEADQUARTERS & DETAILS
                            </span>
                            <h2 className="title-font-bold text-white display-6 mt-1 mb-2">
                                Head Office Location
                            </h2>
                            <div className="contact-center-bar mx-auto mt-3"></div>
                        </div>

                        <div className="row g-4 justify-content-center">
                            {/* Physical Address Card */}
                            <div className="col-12 col-md-4">
                                <div className="contact-translucent-box p-4 h-100 rounded-4 d-flex flex-column justify-content-start">
                                    <div className="contact-icon-wrapper mb-3 d-inline-flex align-items-center justify-content-center rounded-3">
                                        <FaMapMarkerAlt size={22} />
                                    </div>
                                    <h4 className="title-font-semi-bold text-white mb-2">Physical Address</h4>
                                    <p className="font-regular text-white-75 mb-0 small lh-lg">
                                        MWADEO Headquarters,<br />
                                        Main Street, Block B,<br />
                                        Dar es Salaam, Tanzania
                                    </p>
                                </div>
                            </div>

                            {/* Postal Address Card */}
                            <div className="col-12 col-md-4">
                                <div className="contact-translucent-box p-4 h-100 rounded-4 d-flex flex-column justify-content-start">
                                    <div className="contact-icon-wrapper mb-3 d-inline-flex align-items-center justify-content-center rounded-3">
                                        <FaBuilding size={20} />
                                    </div>
                                    <h4 className="title-font-semi-bold text-white mb-2">Postal Address</h4>
                                    <p className="font-regular text-white-75 mb-0 small lh-lg">
                                        P.O. Box 12345,<br />
                                        Dar es Salaam,<br />
                                        United Republic of Tanzania
                                    </p>
                                </div>
                            </div>

                            {/* Area of Operation Card */}
                            <div className="col-12 col-md-4">
                                <div className="contact-translucent-box p-4 h-100 rounded-4 d-flex flex-column justify-content-start">
                                    <div className="contact-icon-wrapper mb-3 d-inline-flex align-items-center justify-content-center rounded-3">
                                        <FaGlobeAfrica size={22} />
                                    </div>
                                    <h4 className="title-font-semi-bold text-white mb-2">Area of Operation</h4>
                                    <p className="font-regular text-white-75 mb-0 small lh-lg">
                                        National Scope:<br />
                                        Operating across all regions in Mainland Tanzania & Zanzibar.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 4. Interactive Contact Form Section */}
                {/* <section className="py-5 bg-white">
                    <div className="container py-4">
                        <div className="row g-5 align-items-center">
                            <div className="col-12 col-lg-5">
                                <span className="badge bg-primary text-white text-uppercase letter-spacing-1 fw-bold px-3 py-2 mb-3">
                                    LEAVE A MESSAGE
                                </span>
                                <h2 className="title-font-bold text-dark display-6 mb-3">
                                    Send Us an Inquiry
                                </h2>
                                <p className="font-regular text-muted lh-lg mb-4">
                                    Have questions about our initiatives, membership, or partnership opportunities? Fill out the form, and our team will get back to you within 24–48 hours.
                                </p>
                                <div className="p-4 rounded-4 bg-light border">
                                    <h6 className="title-font-bold text-dark mb-2">Looking for immediate support?</h6>
                                    <p className="font-regular text-muted small mb-0 lh-base">
                                        Call our official office line directly during working hours for urgent enquiries.
                                    </p>
                                </div>
                            </div>

                            <div className="col-12 col-lg-7">
                                <form onSubmit={handleSubmit} className="p-4 p-md-5 rounded-4 bg-light border shadow-sm">
                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label font-regular small fw-bold text-dark">Your Name</label>
                                            <input 
                                                type="text" 
                                                className="form-control p-3 border-0 bg-white shadow-xs" 
                                                placeholder="John Doe" 
                                                required
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label font-regular small fw-bold text-dark">Email Address</label>
                                            <input 
                                                type="email" 
                                                className="form-control p-3 border-0 bg-white shadow-xs" 
                                                placeholder="john@example.com" 
                                                required
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label font-regular small fw-bold text-dark">Subject</label>
                                            <input 
                                                type="text" 
                                                className="form-control p-3 border-0 bg-white shadow-xs" 
                                                placeholder="Membership / Partnership Inquiry" 
                                                required
                                                value={formData.subject}
                                                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label font-regular small fw-bold text-dark">Message</label>
                                            <textarea 
                                                className="form-control p-3 border-0 bg-white shadow-xs" 
                                                rows="5" 
                                                placeholder="Write your message here..." 
                                                required
                                                value={formData.message}
                                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            ></textarea>
                                        </div>
                                        <div className="col-12 mt-4">
                                            <button type="submit" className="btn btn-primary px-4 py-3 fw-bold d-inline-flex align-items-center gap-2 rounded-3 shadow-sm">
                                                <FaPaperPlane size={16} /> Send Message
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </section> */}

                {/* 5. National Scope Highlight Banner */}
                <section className="contact-bg-section position-relative py-5" style={{ backgroundImage: `url(${communicationBg})` }}>
                    <div className="contact-dark-overlay"></div>
                    <div className="container position-relative z-index-3 py-4 text-center">
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-9">
                                <span className="text-warning text-uppercase letter-spacing-2 fw-bold small">
                                    NATIONWIDE IMPACT
                                </span>
                                <h3 className="title-font-semi-bold text-white mt-2 mb-0 lh-base display-7">
                                    "We operate on a national scale across Tanzania to care for, protect, and empower every community member."
                                </h3>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    } else {
        return (
            <div className="container mSupportLoading d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                {getInlineLoader()}
            </div>
        );
    }
}